import { Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { RootAppState } from '../app.reducer';
import { selectCxbAdminUIDetails } from '../app.selectors';
import { filter, Observable, of, Subject, throwError } from 'rxjs';
import { map, takeUntil, switchMap, catchError } from 'rxjs/operators';
import {
  AdminUiContentGroupAction,
  AdminUiContentGroupType,
  AdminUiDetails,
  AdminUiMenuItemType,
  AdminUiPageActions,
  AdminUiPageType,
  GroupedAdminUiPageActions,
  GroupedAdminUiPageTypes,
} from '../domain/admin-ui-details';
import { PageType } from '../page-edit-screen/page-type';
import { RouterState, selectUrl } from '../router.selectors';

@Injectable({
  providedIn: 'root',
})
export class AdminUiDetailsService implements OnDestroy {
  private readonly onDestroy$ = new Subject<void>();

  private readonly defaultPageActions = {
    isPageViewIconEnabled: false,
    isPlatformEnabled: false,
    isUserSegmentEnabled: false,
    isForcePublishEnabled: false,
  };

  constructor(private readonly store: Store<RootAppState | RouterState>) {}

  public readonly adminUIDetails$ = this.store.select(selectCxbAdminUIDetails).pipe(
    takeUntil(this.onDestroy$),
    filter((data) => !!data)
  );
  public readonly pageMenuItem$ = this.adminUIDetails$.pipe(
    map(({ menuItems }) => menuItems.find((items) => items.type === AdminUiMenuItemType.Page))
  );
  public readonly contentGroupMenuItem$ = this.adminUIDetails$.pipe(
    map(({ menuItems }) => menuItems.find((items) => items.type === AdminUiMenuItemType.ContentGroup))
  );
  public readonly settingsMenuItem$ = this.adminUIDetails$.pipe(
    map(({ menuItems }) => menuItems.find((items) => items.type === AdminUiMenuItemType.Settings))
  );
  public readonly menuEditorMenuItem$ = this.adminUIDetails$.pipe(
    map(({ menuItems }) => menuItems.find((items) => items.type === AdminUiMenuItemType.MenuEditor))
  );

  public readonly osnPlusContentGroupActions$: Observable<AdminUiContentGroupAction> = this.adminUIDetails$.pipe(
    map((adminUIDetails: AdminUiDetails) => {
      const defaultActions = { isDesignatedContentGroupEnabled: false };
      if (!adminUIDetails?.menuItems) {
        return defaultActions;
      }

      return adminUIDetails.menuItems.reduce(
        (actions, menuItem) => {
          const isContentGroup = menuItem.type === AdminUiMenuItemType.ContentGroup;
          const osnPlusContentGroup = menuItem?.contentGroupTypes?.find(
            (contentGroupType) => contentGroupType.type === AdminUiContentGroupType.OsnPlus
          );

          const isOsnPlusContentGroup = !!osnPlusContentGroup?.actions;

          if (isContentGroup && isOsnPlusContentGroup) {
            return osnPlusContentGroup.actions;
          }

          return actions;
        },
        { ...defaultActions }
      );
    })
  );

  public readonly pageActions$: Observable<GroupedAdminUiPageActions> = this.adminUIDetails$.pipe(
    map((adminUIDetails: AdminUiDetails) => {
      const getPageActions = (pageType: AdminUiPageType): AdminUiPageActions => {
        if (!adminUIDetails?.menuItems) {
          return this.defaultPageActions;
        }

        return adminUIDetails.menuItems.reduce(
          (actions, menuItem) => {
            const isPage = menuItem.type === AdminUiMenuItemType.Page;
            const page = menuItem?.pageTypes?.find((adminUiPage) => adminUiPage.type === pageType);

            const hasPageActions = !!page?.actions;

            if (isPage && hasPageActions) {
              return page.actions;
            }

            return actions;
          },
          { ...this.defaultPageActions }
        );
      };

      return {
        content: getPageActions(AdminUiPageType.Content),
        marketing: getPageActions(AdminUiPageType.Marketing),
        redeem: getPageActions(AdminUiPageType.Redeem),
        genre: getPageActions(AdminUiPageType.Genre),
      };
    })
  );

  public readonly pageItemTypes$: Observable<GroupedAdminUiPageTypes> = this.pageMenuItem$.pipe(
    map((items) => {
      const getPageType = (pageType: AdminUiPageType) => items.pageTypes.find((pageTypeItem) => pageTypeItem.type === pageType);
      return {
        content: getPageType(AdminUiPageType.Content),
        marketing: getPageType(AdminUiPageType.Marketing),
        redeem: getPageType(AdminUiPageType.Redeem),
        genre: getPageType(AdminUiPageType.Genre),
      };
    })
  );

  public getPageActionsByType$ = (pageType: PageType): Observable<AdminUiPageActions> => {
    switch (pageType?.stringValue) {
      case 'content':
        return this.pageActions$.pipe(map((actions) => actions.content));
      case 'redeem':
        return this.pageActions$.pipe(map((actions) => actions.redeem));
      case 'appLandingCotv':
      case 'appLandingMobile':
      case 'appLandingTablet':
      case 'marketingDetails':
      case 'appLandingAppleAndroidTv':
      case 'landing':
        return this.pageActions$.pipe(map((actions) => actions.marketing));
      case 'genre':
        return this.pageActions$.pipe(map((actions) => actions.genre));
    }
  };

  public currentPageActions$: Observable<AdminUiPageActions | null> = this.store.select(selectUrl).pipe(
    switchMap((pathname) => {
      const pathSplit = pathname.split('/');
      const pageTypeName = pathSplit[2];
      if (['content', 'marketing', 'genre', 'redeem'].includes(pageTypeName)) {
        return of(pageTypeName);
      }
      return throwError(() => 'Invalid page name');
    }),
    map((pageTypeName) => {
      const stringValue = pageTypeName.replace('/pages/', '').replace('marketing', 'marketingDetails');
      return {
        stringValue,
      };
    }),
    switchMap((pageType: PageType) => {
      return this.getPageActionsByType$(pageType);
    }),
    catchError(() => {
      return of(this.defaultPageActions);
    })
  );

  public ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
